import { ErrorMessage, useFormikContext } from "formik";
import { FormGroup, Label, Input, Row, Col, type InputProps } from "reactstrap";

import CreatorVisibleIndicator from "@/components/Forms/components/CreatorVisibleIndicator";
import CustomSelect from "components/Forms/CustomSelect";
import { countryEnum } from "modules/Creators/constants";

interface CreatorInviteFieldsProps {
  namePrefix?: string;
  disabled?: InputProps["disabled"];
}

/**
 * Reusable component for rendering creator invitation form fields.
 * Uses Formik context to automatically handle form state, validation, and changes.
 *
 * Fields included:
 * - Creator Name (required)
 * - Email Address (required, with email validation)
 * - Phone Number (optional)
 * - Country (required, dropdown)
 * - Account Type (required, Creator/Agency dropdown)
 * - Enable Notifications (checkbox, defaults to true)
 *
 * @param namePrefix - Prefix for field names (default: "invite"). Results in fields like "inviteCreatorName"
 * @param disabled - Whether all fields should be disabled
 *
 * @example
 * ```typescript
 * // Basic usage in a Formik form
 * <Formik initialValues={formValues} onSubmit={handleSubmit}>
 *   <Form>
 *     {isInviteMode && <CreatorInviteFields />}
 *   </Form>
 * </Formik>
 *
 * // With custom prefix and disabled state
 * <CreatorInviteFields
 *   namePrefix="new"
 *   disabled={isLoading}
 * />
 * ```
 */
export const CreatorInviteFields = ({
  namePrefix = "invite",
  disabled = false,
}: CreatorInviteFieldsProps) => {
  const { values, errors, touched, handleChange } = useFormikContext<any>();

  const getFieldName = (field: string) =>
    `${namePrefix}Creator${field.charAt(0).toUpperCase() + field.slice(1)}`;

  return (
    <>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor={getFieldName("name")}>
              <CreatorVisibleIndicator
                isVisible={true}
                showIcon={true}
                iconPosition="static"
              >
                Creator Name
              </CreatorVisibleIndicator>
            </Label>
            <Input
              id={getFieldName("name")}
              name={getFieldName("name")}
              type="text"
              value={values[getFieldName("name")] || ""}
              onChange={handleChange}
              invalid={
                !!(
                  errors[getFieldName("name")] && touched[getFieldName("name")]
                )
              }
              disabled={disabled}
              placeholder="Enter creator name"
            />
            <ErrorMessage
              name={getFieldName("name")}
              component="div"
              className="text-danger"
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor={getFieldName("email")}>
              <CreatorVisibleIndicator
                isVisible={true}
                showIcon={true}
                iconPosition="static"
              >
                Email Address
              </CreatorVisibleIndicator>
            </Label>
            <Input
              id={getFieldName("email")}
              name={getFieldName("email")}
              type="email"
              value={values[getFieldName("email")] || ""}
              onChange={handleChange}
              invalid={
                !!(
                  errors[getFieldName("email")]
                  && touched[getFieldName("email")]
                )
              }
              disabled={disabled}
              placeholder="creator@example.com"
            />
            <ErrorMessage
              name={getFieldName("email")}
              component="div"
              className="text-danger"
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor={getFieldName("phone")}>
              <CreatorVisibleIndicator
                isVisible={true}
                showIcon={true}
                iconPosition="static"
              >
                Phone Number
              </CreatorVisibleIndicator>
            </Label>
            <Input
              id={getFieldName("phone")}
              name={getFieldName("phone")}
              type="text"
              value={values[getFieldName("phone")] || ""}
              onChange={handleChange}
              invalid={
                !!(
                  errors[getFieldName("phone")]
                  && touched[getFieldName("phone")]
                )
              }
              disabled={disabled}
              placeholder="(555) 123-4567"
            />
            <ErrorMessage
              name={getFieldName("phone")}
              component="div"
              className="text-danger"
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor={getFieldName("country")}>
              <CreatorVisibleIndicator
                isVisible={true}
                showIcon={true}
                iconPosition="static"
              >
                Country
              </CreatorVisibleIndicator>
            </Label>
            <CustomSelect
              name={getFieldName("country")}
              options={countryEnum}
              placeholder="Select country..."
              isDisabled={disabled}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label htmlFor={getFieldName("isAgency")}>Account Type</Label>
            <CustomSelect
              name={getFieldName("isAgency")}
              options={[
                { value: false, label: "Creator" },
                { value: true, label: "Agency" },
              ]}
              placeholder="Select account type..."
              isDisabled={disabled}
            />
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <div className="form-check mt-4">
              <Input
                id={getFieldName("enableNotifications")}
                name={getFieldName("enableNotifications")}
                type="checkbox"
                className="form-check-input"
                checked={values[getFieldName("enableNotifications")] || false}
                onChange={handleChange}
                disabled={disabled}
              />
              <Label
                className="form-check-label"
                htmlFor={getFieldName("enableNotifications")}
              >
                <CreatorVisibleIndicator
                  isVisible={true}
                  showIcon={true}
                  iconPosition="static"
                >
                  Enable email notifications
                </CreatorVisibleIndicator>
              </Label>
            </div>
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};
